import { TitleCase } from '../../Helpers/Calculations';
import { isMinor } from '../../Helpers/Helpers';


const digitalFormsBaseUrl = `DigitalForms`;

export const getHealthWellFunds = async (axiosGet, assistanceProgramId) => {
    return await axiosGet(`${digitalFormsBaseUrl}/HealthWellFunds/${assistanceProgramId}`);
}

export const getHealthWellFundMedications = async (axiosGet, assistanceProgramId) => {
    return await axiosGet(`${digitalFormsBaseUrl}/FoundationFundMedications/${assistanceProgramId}`);
}

const runHealthWellAssistanceTypeRule = (meta, ctx) => {
    const healthWellAssistanceTypeField = 'HealthWell Other Assistance Type';

    if (!ctx.getFieldDef(healthWellAssistanceTypeField)) { return; }

    // assistance service names values are 'Co-Pay Assistance' & 'Premuim Assistance'
    // question options are 'Copay Assistance' & 'Premium Assistance'
    const stripped = meta.assistanceServices[0].name
        .toLowerCase()
        .replace('-', '');

    const normalizedServiceName = TitleCase(stripped);
    ctx.specialRuleChangeData(healthWellAssistanceTypeField, normalizedServiceName);
}

const healthWellFundRequiresInsuranceSpecialRule = (value, ctx) => {
    const insuranceField = 'HealthWell Other Insurance for Requested Therapy';
    const field = ctx.getFieldDef(insuranceField);

    if (field && value?.toLowerCase() === 'no') {
        ctx.specialRuleChangeData(insuranceField, value, 'Patient must have insurance for requested therapy to apply.');
    }
}

const healthWellPrimaryInsuranceRule = (value, ctx) => {
    const primaryInsuranceField = 'Insurance Primary Type';
    const field = ctx.getFieldDef(primaryInsuranceField);
    if (field) {
        ctx.specialRuleChangeData('HealthWell Fund Applying For', undefined);
    }
}

const healthWellPatientRecievingOtherAssistanceSpecialRule = (value, ctx) => {
    const assistanceField = 'HealthWell Other Receiving Assistance from Another Copay Foundation';
    const field = ctx.getFieldDef(assistanceField);

    if (field && value?.toLowerCase() === 'yes') {
        ctx.specialRuleChangeData(assistanceField, value, 'Patient is not eligible if already receiving assistance.');
    }
}

const healthWellPremiumAttestationRule = (value, ctx) => {
    const diseaseStateField = 'Other Premium Maintenance Medication Attestation';
    const field = ctx.getFieldDef(diseaseStateField);

    if (field && value?.toLowerCase() === 'no') {
        ctx.specialRuleChangeData(diseaseStateField, value, 'Based on the answers provided, the applicant is ineligible for a premium assistance grant.');
    }
}

const initHealthWellAuthorizedContactRule = (ctx) => {
    const patientDOB = 'Patient DOB';
    const parentDOB = 'Parent DOB';
    ctx.createSpecialRule(patientDOB, healthWellAuthorizedContactRule);
    ctx.createSpecialRule(parentDOB, healthWellAuthorizedContactUnder18Rule);

    if (patientDOB) {
        const patientDOBAsDate = new Date(ctx.formData[patientDOB]);
        healthWellAuthorizedContactRule(patientDOBAsDate, ctx);
    }

    if (parentDOB) {
        const parentDOBAsDate = new Date(ctx.formData[parentDOB]);
        healthWellAuthorizedContactUnder18Rule(parentDOBAsDate, ctx);
    }
}

// these fields should only be visible and required if patient is a minor
const healthWellAuthorizedContactRule = (value, ctx) => {
    if (!value) return;

    const parentFirstName = 'Parent Name First';
    const parentLastName = 'Parent Name Last';
    const parentDOB = 'Parent DOB';
    const parentSSN = 'Parent SSN';

    const action = isMinor(value) ? 'show' : 'hide';

    ctx.specialRulePerformAction(action, [parentFirstName, parentLastName, parentDOB, parentSSN]);
}

const healthWellAuthorizedContactUnder18Rule = (value, ctx) => {
    if (!value) return;

    const parentDOB = 'Parent DOB';

    if (isMinor(value)) {
        ctx.specialRuleChangeData(parentDOB, value, 'Parent is under 18 years old.');
    }
}

const healthWellStreetAddressPOBoxSpecialRule = (value, ctx) => {
    if (!value) return;

    const invalidCharsReg = /[^a-zA-Z0-9 ]/gm;
    const hasInvalidCharacters = value.match(invalidCharsReg);

    if (hasInvalidCharacters) {
        const fieldIndex = Object.values(ctx.formData).indexOf(value);
        const fieldName = Object.keys(ctx.formData)[fieldIndex];
        ctx.specialRuleChangeData(fieldName, value, 'Please remove special characters as they are not acceptable in the provider portal.');
    }
    else {
        const POBoxCharsReg = /(?:Post(?:al)? (?:Office )?|P[. ]?O\.? )+Box(?:\s*)(?:#?\d+\w*\s*)*/gmi;
        const hasPOBox = value?.match(POBoxCharsReg);

        if (hasPOBox) {
            ctx.specialRuleChangeData('Patient Address 1', value, 'PO Box is not permitted in the Physical Address field. Please move this to the PO Box address section.');
        }
    }
}

export const createHealthwellRules = (meta, ctx) => {
    runHealthWellAssistanceTypeRule(meta, ctx);
    initHealthWellAuthorizedContactRule(ctx);

    ctx.createSpecialRule('Other Premium Maintenance Medication Attestation', healthWellPremiumAttestationRule);
    ctx.createSpecialRule('HealthWell Other Insurance for Requested Therapy', healthWellFundRequiresInsuranceSpecialRule);
    ctx.createSpecialRule('HealthWell Other Receiving Assistance from Another Copay Foundation', healthWellPatientRecievingOtherAssistanceSpecialRule);
    ctx.createSpecialRule('Patient Address 1', healthWellStreetAddressPOBoxSpecialRule);
    ctx.createSpecialRule('Insurance Primary Type', healthWellPrimaryInsuranceRule);

    // Run all the special rules to make sure a loaded draft loads with any necessary errors
    healthWellFundRequiresInsuranceSpecialRule(ctx.formData['HealthWell Other Insurance for Requested Therapy'], ctx);
    healthWellPatientRecievingOtherAssistanceSpecialRule(ctx.formData['HealthWell Other Receiving Assistance from Another Copay Foundation'], ctx);
    healthWellPremiumAttestationRule(ctx.formData['Other Premium Maintenance Medication Attestation'], ctx);
    healthWellStreetAddressPOBoxSpecialRule(ctx.formData['Patient Address 1'], ctx);
    healthWellPrimaryInsuranceRule(ctx.formData['Insurance Primary Type'], ctx);
}
